import { useEffect, useMemo } from 'react';

import type { GenerateReferralUrlParams } from '@/ducks/programs/celebrations/types';
import type { TSailingData } from '@/infra/types/voyageInfo/sailing';
import type { TOptional } from '@/types/common';

import { selectConfigServiceFeatureCelebrations } from '@/ducks/common/settings/selectors';
import { generateReferralUrlActionForCelebrations } from '@/ducks/programs/celebrations/actions';
import { selectCelebrationsParams } from '@/ducks/programs/celebrations/selectors';
import { selectSailingData } from '@/ducks/voyageInfo/sailing/selectors';
import { useAppDispatch, useAppSelector } from '@/store';

export type TShareReferral = {
  link: TOptional<string>;
  sailingData: TOptional<TSailingData>;
};

const useShareReferral = (reservationNumber: string): TShareReferral => {
  const dispatch = useAppDispatch();
  const sailingData = useAppSelector(selectSailingData);
  const { packageCode, ship, voyageId } = sailingData || {};
  const isAllowCelebrations = useAppSelector(selectConfigServiceFeatureCelebrations);
  const { newReferralCode, referralUrl: celebrationReferralUrl } = useAppSelector(selectCelebrationsParams) || {};

  useEffect(() => {
    if (!reservationNumber || !voyageId || !packageCode || !ship?.code || newReferralCode) {
      return;
    }

    const params: GenerateReferralUrlParams = {
      packageCode,
      reservationNumber,
      shipCode: ship.code,
      voyageId,
    };

    if (isAllowCelebrations) {
      dispatch(generateReferralUrlActionForCelebrations(params));
    }
  }, [dispatch, reservationNumber, voyageId, packageCode, ship?.code, isAllowCelebrations, newReferralCode]);

  return useMemo(
    () => ({
      link: isAllowCelebrations ? celebrationReferralUrl : undefined,
      sailingData,
    }),
    [celebrationReferralUrl, isAllowCelebrations, sailingData],
  );
};

export default useShareReferral;
